'use client';
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, CircularProgress, Grid, TextField, Stack, Button } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog'; // <-- Pastikan path benar

import { getAreaById, createArea, updateArea } from '../../../stores/areaStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import { useCountryList } from '../../../stores/countryStore';
import usePickerPagination from '../../../hooks/usePickerPagination';
import MasterCountryAddEdit from '../country/MasterCountryAddEdit';
import { useNotifier } from '../../../components/Notifier';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import { useLanguage } from '../../../utils/i18n';

const MasterAreaAddEdit = forwardRef(({ open, id, onClose, onSuccess, onError, onSubmitStart = () => {}, onSubmitEnd = () => {} }, ref) => {
  const [initialValues, setInitialValues] = useState({
    company: '',
    name: '',
    country: '',
    city: ''
  });
  const { t } = useLanguage();
  const notify = useNotifier();

  const [loading, setLoading] = useState(false);
  const formikRef = useRef(null);

  const [filterCompanyId, setFilterCompanyId] = useState('');

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const countryPickerParams = usePickerPagination({
    fetchHook: useCountryList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, state.companyId],
    searchKey: 'name',
    companyId: filterCompanyId,
    formatOption: (item) => ({
      value: item?.id ?? item?.value ?? '',
      label: item?.name ?? item?.country ?? String(item?.id ?? item?.value ?? '')
    })
  });

  useImperativeHandle(ref, () => ({
    submitForm: () => formikRef.current?.submitForm?.(),
    getValues: () => formikRef.current?.values
  }));

  const [initialCountryOption, setInitialCountryOption] = useState(null);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (!open) return; // <-- Cegah fetch jika modal belum terbuka

      setLoading(true);
      try {
        if (id) {
          const data = await getAreaById(id);
          if (!mounted) return;

          if (data) {
            const companyId = data.company ?? data.company_id ?? '';
            setFilterCompanyId(String(companyId));

            if (data.country) {
              setInitialCountryOption({
                value: data.country,
                label: data.join?.country_name || String(data.country)
              });
            } else {
              setInitialCountryOption(null);
            }

            setInitialValues({
              company: String(companyId),
              name: data.name ?? '',
              country: data.country ?? '',
              city: data.city ?? ''
            });
          }
        } else {
          const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
          if (stored) {
            const storedStr = String(stored);
            setFilterCompanyId(storedStr);
            setInitialValues((prev) => ({
              ...prev,
              company: storedStr,
              name: '',
              country: '',
              city: ''
            }));
          } else {
            setInitialValues({
              company: '',
              name: '',
              country: '',
              city: ''
            });
          }
        }
      } catch (err) {
        console.error('Failed to init form:', err);
        onError && onError('Failed to load data');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    init();
    return () => {
      mounted = false;
    };
  }, [id, onError, open]); // <-- Hapus onError dari dependency agar tidak re-render berkali-kali

  useEffect(() => {
    if (id) return;
    const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
    if (!stored) return;

    setFilterCompanyId(String(stored));
    const matchedCompany = companyPickerParams.options.find((c) => String(c.value) === String(filterCompanyId))?.value;

    if (!matchedCompany && filterCompanyId) {
      // Tunggu hingga options terisi
      return;
    }

    setInitialValues((prev) => ({
      ...prev,
      company: matchedCompany
    }));
  }, [companyPickerParams.options, id, filterCompanyId]);

  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required('Company is required'),
    name: Yup.string().required('Name is required'),
    country: Yup.string().required('Country is required'),
    city: Yup.string().required('City is required')
  });

  const handleClose = () => {
    formikRef.current?.resetForm();
    if (onClose) onClose();
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    onSubmitStart();
    try {
      const payload = {
        company: Number(values.company),
        name: formatUpperCase(values.name),
        country: Number(values.country),
        city: values.city
      };
      let response;
      if (id) {
        response = await updateArea(id, payload);
        onSuccess && onSuccess('Updated successfully');
      } else {
        response = await createArea(payload);
        onSuccess && onSuccess('Created successfully');
      }
      if (response?.code === 400 || response?.status === false || response?.error) {
        throw response;
      }
      resetForm();
      if (onClose) onClose();
    } catch (err) {
      console.error('Submit error:', err);
      const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
      notify({ message: errorMessage, severity: 'error' });
    } finally {
      setSubmitting(false);
      onSubmitEnd();
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
        <FormDialog
          open={open}
          onClose={handleClose}
          title={id ? t('workingArea.edit', 'Edit Master Area') : t('workingArea.add', 'Add Master Area')}
          maxWidth="xs"
          fullHeight={false}
          actions={
            <>
              <Button
                variant="contained"
                type="submit"
                form="master-area-form" // <-- Ini mentrigger form submission
                disabled={isSubmitting || loading}
                sx={{ backgroundColor: '#0072DA', textTransform: 'none' }}
                startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
              </Button>
              <Button onClick={handleClose} disableRipple disableElevation variant="outlined" sx={{ textTransform: 'none' }}>
                {t('buttonTitle.cancel', 'Cancel')}
              </Button>
            </>
          }
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: '100%', maxWidth: 920 }}>
              <form id="master-area-form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item size={{ xs: 12 }}>
                    <Stack spacing={2}>
                      <PickerField
                        label={t('general.company', 'Company')}
                        value={values.company}
                        {...companyPickerParams}
                        onChange={(opt) => {
                          const val = typeof opt === 'object' ? opt?.value : opt;
                          setFieldValue('company', val);
                          setFilterCompanyId(val);
                          setFieldValue('country', '');
                        }}
                        error={touched.company && !!errors.company}
                        helperText={touched.company && errors.company}
                      />

                      <TextField
                        fullWidth
                        size="medium"
                        id="name"
                        name="name"
                        label={`${t('workingArea.name', 'Name')} *`}
                        value={values.name}
                        onChange={(e) => {
                          setFieldValue('name', formatUpperCase(e.target.value));
                        }}
                        onBlur={handleBlur}
                        error={touched.name && !!errors.name}
                        helperText={touched.name && errors.name}
                        InputLabelProps={{
                          sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
                        }}
                      />

                      <PickerField
                        label={t('workingArea.country', 'Country')}
                        value={values.country}
                        selectedItems={initialCountryOption ? [initialCountryOption] : []}
                        {...countryPickerParams}
                        disabled={!filterCompanyId}
                        onChange={(opt) => {
                          const val = typeof opt === 'object' ? opt?.value : opt;
                          setFieldValue('country', val);
                        }}
                        allowAdd={true}
                        addDialogProps={{ fullScreen: false, maxWidth: 'md', fullHeight: false }}
                        AddComponent={MasterCountryAddEdit}
                        dialogTitle={t('general.selectCountry', 'Select Country')}
                        getOptionId={(o) => o?.value}
                        getOptionLabel={(o) => o?.label}
                        error={touched.country && !!errors.country}
                        helperText={
                          !values.company
                            ? t('general.selectCompanyFirst', '* Please select a company first')
                            : touched.country && errors.country
                        }
                      />

                      <TextField
                        fullWidth
                        size="medium"
                        id="city"
                        name="city"
                        label={`${t('workingArea.city', 'City')} *`}
                        value={values.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.city && !!errors.city}
                        helperText={touched.city && errors.city}
                        InputLabelProps={{
                          sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
                        }}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            </Box>
          )}
        </FormDialog>
      )}
    </Formik>
  );
});

export default MasterAreaAddEdit;
